/**
 * TASK-589 / API 3 — Open Autoshops Voice.
 * Happy path: SSO mint → {host}/sso/callback?token=…
 * Fallback: partner /accept-invite only (never EZ /login or /register).
 */
import getApiBase from "../components/getApiBase";

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function isAcceptInviteUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    return u.pathname.includes("accept-invite");
  } catch {
    return String(url).includes("accept-invite");
  }
}

function isPasswordLoginUrl(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    return (
      u.pathname === "/login" ||
      u.pathname.endsWith("/login") ||
      u.pathname.includes("/register")
    );
  } catch {
    return /\/login(\?|$)/.test(url) || /\/register/.test(url);
  }
}

async function mintVoiceSsoUrl(shopId) {
  const res = await fetch(`${getApiBase()}/api/ezvoice/shops/${shopId}/sso`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: "{}",
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `Voice SSO failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  if (!body.ssoUrl) {
    throw new Error("Voice SSO succeeded but no ssoUrl was returned");
  }
  return body.ssoUrl;
}

async function resolveAcceptInviteUrl(shopId) {
  const statusRes = await fetch(`${getApiBase()}/api/ezvoice/shops/${shopId}/status`, {
    headers: authHeaders(),
    credentials: "include",
  });
  if (!statusRes.ok) {
    const body = await statusRes.json().catch(() => ({}));
    throw new Error(body.error || `Voice status failed (${statusRes.status})`);
  }
  const status = await statusRes.json();

  let url = status.onboardingUrl || null;
  if (!url || isPasswordLoginUrl(url)) {
    const res = await fetch(
      `${getApiBase()}/api/ezvoice/shops/${shopId}/resend-onboarding`,
      {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: "{}",
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.error || `Could not get Voice invite (${res.status})`);
    }
    url = body.onboardingUrl || body.status?.onboardingUrl || null;
  }

  if (!isAcceptInviteUrl(url) || isPasswordLoginUrl(url)) {
    return null;
  }
  return url;
}

/**
 * Prefer API 3 SSO; fall back to accept-invite only when SSO is not configured (503).
 * Never open Voice /login — that is the pink-snag + password path we are retiring.
 */
export async function openVoicePartnerOnboarding(shopId) {
  if (!shopId) {
    throw new Error("Missing shop for Voice setup");
  }

  try {
    const ssoUrl = await mintVoiceSsoUrl(shopId);
    window.open(ssoUrl, "_blank", "noopener,noreferrer");
    return ssoUrl;
  } catch (ssoErr) {
    const status = ssoErr?.status;
    // Only fall back when SSO secret/host is missing. Other failures must surface.
    if (status !== 503) {
      throw ssoErr;
    }
  }

  const url = await resolveAcceptInviteUrl(shopId);
  if (!url) {
    throw new Error(
      "Voice SSO is not ready yet (or Owner is already activated). Ask platform admin to confirm PARTNER_SSO_SHARED_SECRET on Autoshops BE + Voice Railway staging, then try Open Voice Assistant again.",
    );
  }

  window.open(url, "_blank", "noopener,noreferrer");
  return url;
}
